import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import FormControl from '../../../../../Components/Form/FormControl';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import ElectronicPrescriptionDetailLatamAddMedicamentForm, {
	ElectronicPrescriptionDetailLatamAddMedicamentFormValues,
} from './ElectronicPrescriptionDetailLatamAddMedicamentForm';
import ElectronicPrescriptionDetailLatamMedicamentsList from './ElectronicPrescriptionDetailLatamMedicamentsList';
import TextArea from '../../../../../Components/Base/TextArea/TextArea';
import DatePicker from '../../../../../Components/Base/DatePicker/DatePicker';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { getMomentDate } from '../../../../../Utils/dates.util';

const validationSchema = yup.object({
	nonPharmacologicalTreatment: yup.string().notRequired(),
	expirationDate: yup.date().required('Fecha de vencimiento de la receta'),
});

interface ElectronicPrescriptionDetailLatamProps {
	onSubmit?: any;
	defaultValues?: any;
	disableSubmit?: boolean;
}

const ElectronicPrescriptionDetailLatam: React.FC<
	ElectronicPrescriptionDetailLatamProps
> = ({ onSubmit, defaultValues, disableSubmit = false }) => {
	const [medicaments, setMedicaments] = useState<
		ElectronicPrescriptionDetailLatamAddMedicamentFormValues[]
	>([]);
	const [editMedicament, setEditMedicament] = useState<
		ElectronicPrescriptionDetailLatamAddMedicamentFormValues | undefined
	>(undefined);

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	useEffect(() => {
		if (defaultValues?.items?.length) {
			//@ts-ignore
			const medicamentsItems = defaultValues.items.map(({ extra }) => ({
				amount: extra?.amount,
				concentration: extra?.concentration,
				doseFrequency: extra?.doseFrequency,
				doseFrequencyPer: extra?.doseFrequencyPer,
				medicamentType: extra?.medicamentType,
				medicine: extra?.medicine,
				observations: extra?.observations,
				period: extra?.period,
				periodPer: extra?.periodPer,
				presentation: extra?.presentation,
				referredDiagnosis: extra?.referredDiagnosis,
				referredDiagnosisId: extra?.referredDiagnosisId,
				routeOfAdministration: extra?.routeOfAdministration,
				medicationCode: extra?.medicationCode,
			}));

			setMedicaments(medicamentsItems);
		}
	}, [defaultValues]);

	const handleSubmit = async (values: any) => {
		const expirationDate = getMomentDate(values.expirationDate);

		values.expirationDate = expirationDate.format('yyyy-MM-DD');

		const finalValues = {
			...values,
			addToPatientECard: true,
			medicaments: medicaments.map(medicament => {
				const finalMedicament: any = medicament;
				delete finalMedicament.id;
				finalMedicament.routeOfAdministration =
					finalMedicament.routeOfAdministration?.label || null;
				finalMedicament.doseFrequencyPer =
					finalMedicament.doseFrequencyPer?.label || null;
				finalMedicament.periodPer = finalMedicament.periodPer?.label || null;
				finalMedicament.presentation =
					finalMedicament.presentation?.label || null;
				finalMedicament.referredDiagnosisId =
					finalMedicament.referredDiagnosis?.code || null;
				return finalMedicament;
			}),
		};

		onSubmit && (await onSubmit(finalValues));

		formMethods.reset();
		setMedicaments([]);
	};

	const handleAddMedicament = (
		values: ElectronicPrescriptionDetailLatamAddMedicamentFormValues,
	) => {
		setMedicaments(prev => {
			const old = prev.filter(m => m.id !== values.id);
			return [...old, values];
		});
	};

	const handleRemoveMedicament = (id: string) => {
		setMedicaments(medicaments.filter(m => m.id !== id));
	};

	const handleEditMedicament = (id: string) => {
		setEditMedicament(medicaments.find(m => m.id === id));
	};

	return (
		<>
			<ElectronicPrescriptionDetailLatamAddMedicamentForm
				onSubmit={handleAddMedicament}
				medicament={editMedicament}
			/>
			<ElectronicPrescriptionDetailLatamMedicamentsList
				data={medicaments}
				onRemove={handleRemoveMedicament}
				onEdit={handleEditMedicament}
			/>
			<FormWrapper formMethods={formMethods}>
				<FormControl
					name='nonPharmacologicalTreatment'
					label='component.electronicPrescriptionDetailLatam.nonPharmacologicalTreatment'>
					<TextArea />
				</FormControl>
				<div className={'row mb-3'}>
					<div className={'col-lg-3 col-12'}>
						<FormControl
							name='expirationDate'
							label='component.electronicPrescriptionDetailLatam.expirationDate'
							isRequired={true}>
							<DatePicker minDate={moment()} />
						</FormControl>
					</div>
				</div>
				<div className='mt-4'>
					<SubmitButton
						title='component.electronicPrescriptionDetailLatam.savePrescription'
						onSubmit={handleSubmit}
						disabled={disableSubmit || medicaments.length === 0}
					/>
				</div>
			</FormWrapper>
		</>
	);
};

export default ElectronicPrescriptionDetailLatam;
